import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSetRecoilState, useRecoilValue } from "recoil";
import {
  TournamentResponse,
  getStructuresByCafeId,
  TimerDto,
  removeTournament,
} from "../../../api/tournament";
import { PaginationWrapper } from "../../../component/CommonTable";
import PopupPagination from "../../../component/Pagination/popupPagination";
import MobileHeader from "../../../layout/mobile-header";
import { dialogState } from "../../../recoil/dialog.atom";
import { pubState } from "../../../recoil/pub.atom";
import { WideState } from "../../../recoil/wide";
import { getResultTime } from "../../../utils/timer";
import { calculateHomeLevel } from "../../timer-controller/hooks/timer";
import { TournamentProvider } from "../hook/context";
import { StructuresResponse } from "../hook/useEditTournament";
import {
  MobileTournamentWrapper,
  MobileTournamentBackground,
  MobileTournamentTableWrapper,
  MobileTournamentItem,
  MobileTournamentItemHeader,
  MobileTournamentItemDropBox,
} from "../style/mobile";
import MobileTournamentEdit from "./mobile-edit";
import MobileTournamentMore from "./mobile-info";

const MobileTournamentList = () => {
  const [tournamentPage, setTournamentPage] = useState<
    "list" | "info" | "edit"
  >("list");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [list, setList] = useState<TournamentResponse[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number[]>([]);
  const setDialog = useSetRecoilState(dialogState);
  const pub = useRecoilValue(pubState);
  const [moreIndex, setMoreIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const [selectTournament, setSelectTournament] =
    useState<TournamentResponse | null>(null);
  const isWide = useRecoilValue(WideState);
  const limit = isWide ? 10 : 5;

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("page") as "list" | "info" | "edit" | null;

  const handlePage = (page: "list" | "info" | "edit") => {
    setTournamentPage(page);
    if (page === "list") {
      searchParams.delete("page");
    } else {
      searchParams.set("page", page);
    }
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (pageParam) {
      setTournamentPage(pageParam);
    } else {
      setTournamentPage("list");
    }
  }, [pageParam]);

  const getList = async () => {
    if (!pub) {
      return;
    }

    const res = await getStructuresByCafeId(
      pub.id,
      currentPage,
      limit,
      "SOLUTION",
      "all",
    );
    setTotalPage(Math.ceil(res.total / limit));
    setList(res.tournaments);
  };

  const onPageChange = (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    getList();
  }, [currentPage]);

  const getLevel = (timerDto: TimerDto, levels: StructuresResponse[]) => {
    const now = new Date();
    const updatedAt = new Date(timerDto.updatedAt);
    const elapsedTimeMs =
      now.getTime() - updatedAt.getTime() + timerDto.playTime;

    const { currentLevel } = calculateHomeLevel(elapsedTimeMs, levels);

    return currentLevel;
  };

  const handleSelect = (id: number) => {
    setSelectedIndex((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const getTournamentStateText = (item: TournamentResponse) => {
    if (!item.timerDto) {
      return item.isEnd ? "종료" : "시작 준비 중";
    }
    if (item.timerDto) {
      if (item.timerDto.isStop) return "지연";
      if (item.structure) {
        if (getResultTime(item) < 0) return "시작 준비 중";
        return getLevel(item.timerDto, item.structure)?.level
          ? "LV " + getLevel(item.timerDto, item.structure)?.level
          : "BREAK";
      }
    }
    return "종료";
  };

  const getTournamentStateClassName = (item: TournamentResponse) => {
    if (!item.timerDto) {
      return item.isEnd ? "end" : "starting";
    }
    if (item.timerDto) {
      if (item.timerDto.isStop) return "stop";
      if (item.structure) {
        if (getResultTime(item) < 0) return "starting";
        return "lv";
      }
    }
    return "end";
  };

  const handleDelete = async (id: number) => {
    setDialog({
      title: "정말 삭제하시겠습니까?",
      confirm: async () => {
        try {
          if (selectedIndex.includes(id)) {
            handleSelect(id);
          }
          await removeTournament(id);
          await getList();
          setMoreIndex(null);
        } catch (e: any) {
          alert(e.message);
          return;
        }
      },
    });
    setMoreIndex(null);
  };

  return (
    <>
      {tournamentPage === "info" && (
        <MobileTournamentMore
          onClose={() => handlePage("list")}
          tournament={selectTournament}
        />
      )}
      {tournamentPage === "edit" && (
        <TournamentProvider>
          <MobileTournamentEdit
            onClose={() => handlePage("list")}
            tournamentId={selectTournament?.id || null}
          />
        </TournamentProvider>
      )}
      {tournamentPage === "list" && (
        <MobileTournamentWrapper onClick={() => setMoreIndex(null)}>
          <MobileHeader
            title="모바일 셀러"
            button={{
              name: "토너먼트 추가하기",
              onClick: () => {
                handlePage("edit");
                setSelectTournament(null);
              },
            }}
          />
          <MobileTournamentBackground>
            <MobileTournamentTableWrapper>
              {list.map((item, index) => (
                <MobileTournamentItem key={index}>
                  <MobileTournamentItemHeader>
                    <div className={`des ${getTournamentStateClassName(item)}`}>
                      {getTournamentStateText(item)}
                    </div>
                    <div className="right">
                      <MobileTournamentItemDropBox>
                        <img
                          src="/image-web/tournament/more.svg"
                          alt="more"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMoreIndex(index === moreIndex ? null : index);
                          }}
                        />
                        {moreIndex === index && (
                          <div className="wrapper">
                            <div
                              className="item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectTournament(item);
                                handlePage("info");
                                setMoreIndex(null);
                              }}
                            >
                              상세정보
                            </div>
                            <div
                              className="item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectTournament(item);
                                handlePage("edit");
                                setMoreIndex(null);
                              }}
                            >
                              수정하기
                            </div>
                            <div
                              className="item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item.id);
                              }}
                            >
                              삭제하기
                            </div>
                          </div>
                        )}
                      </MobileTournamentItemDropBox>
                      <img
                        src="/image-web/tournament/game-timer.svg"
                        onClick={() => {
                          navigate(`/timer-config/${item.id}`);
                        }}
                        alt="game-timer"
                      />
                    </div>
                  </MobileTournamentItemHeader>
                  <div className="title">{item.title}</div>
                </MobileTournamentItem>
              ))}
            </MobileTournamentTableWrapper>
            {totalPage > 1 && (
              <PaginationWrapper>
                <PopupPagination
                  currentPage={currentPage}
                  totalPages={totalPage}
                  onPageChange={onPageChange}
                />
              </PaginationWrapper>
            )}
          </MobileTournamentBackground>
        </MobileTournamentWrapper>
      )}
    </>
  );
};

export default MobileTournamentList;
